import { LayoutSidebarReverse } from "react-bootstrap-icons";
import SchedulePanel from "./SchedulePanel";
import ActivityPanel from "./ActivityPanel";
import MetricsPanel from "./MetricsPanel";
import { useActivityFeed } from "../hooks/useActivityFeed";
import { useActivityDetail } from "../hooks/useActivityDetail";

// Fixed rail width; only collapse (not resize) is offered.
const RAIL_WIDTH = 440;
const RAIL_COLLAPSED_WIDTH = 48;

// The Ontik intelligence rail: schedule, activity feed and impact metrics. Lifted out of the
// Today's tasks page so it can dock on the right of every page (gated by the AI-features flag).
// The floating Ontik button stays put over the app; the rail just leaves bottom padding so its
// scrolled content clears it.
export default function IntelligenceRail({ collapsed, onToggleCollapse }) {
  const {
    items: activity,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useActivityFeed();

  const { onSelect: onSelectActivity, modals } = useActivityDetail();

  return (
    <>
      {/* The rail is a bounded column: Ontik metrics and Scheduled take the height they need at the
          top, Activity fills the rest and scrolls inside itself beneath them. */}
      <aside
        style={{ width: collapsed ? RAIL_COLLAPSED_WIDTH : RAIL_WIDTH }}
        className="relative hidden shrink-0 flex-col overflow-hidden border-l border-zinc-200 bg-zinc-50 transition-[width] duration-300 ease-in-out lg:flex"
      >
        {/* One persistent toggle, right-anchored so it holds position while the left edge animates.
            This row matches the page header (h-12, bordered) so the Ontik title and toggle line up
            with it. */}
        <div className="flex h-12 shrink-0 items-center border-b border-zinc-200 px-3">
          {!collapsed && <h4 className="pl-3 text-sm font-semibold text-zinc-800">Ontik</h4>}
          <button
            type="button"
            onClick={onToggleCollapse}
            title={collapsed ? "Show schedule" : "Hide schedule"}
            aria-label={collapsed ? "Show schedule" : "Hide schedule"}
            className="ml-auto flex size-8 items-center justify-center rounded-md text-zinc-500 hover:bg-zinc-200 hover:text-zinc-700"
          >
            <LayoutSidebarReverse className="size-4" />
          </button>
        </div>

        {!collapsed && (
          // Metrics and schedule take the height they need at the top (schedule caps and scrolls
          // within itself); Activity grows to fill the rest down to the bottom of the rail, scrolling
          // inside its own card. When the viewport is too short to hold all three, the column itself
          // scrolls (overflow-y-auto) so the bottom stays reachable. Clearance for the floating Ontik
          // button lives inside the Activity card's scroll area (see ActivityPanel), so the card can
          // reach the rail's bottom edge while its rows never slide under the button.
          <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-6 pb-4 pt-6">
            <MetricsPanel />
            <SchedulePanel />
            <ActivityPanel
              items={activity}
              onSelect={onSelectActivity}
              onLoadMore={fetchNextPage}
              hasMore={hasNextPage}
              isLoadingMore={isFetchingNextPage}
            />
          </div>
        )}
      </aside>

      {modals}
    </>
  );
}
